import { useRef } from "react";

/** The plain counter behind useRunToken (separate so it can be tested without React). */
export function createRunToken() {
  let counter = 0;
  return {
    /** Begins a run; any earlier run stops being current. */
    start: () => ++counter,
    /** Ends the current run (Stop, Reset, new file, option changed). */
    cancel: () => {
      counter++;
    },
    isCurrent: (run: number) => run === counter,
  };
}

/**
 * One number per run of a long job (convert, compress, OCR...), so a job that was stopped, reset or
 * replaced can never write its late result over the one on screen.
 *
 *   const runs = useRunToken();
 *   const run = runs.start();                 // at the start of the job; also ends any earlier job
 *   const out = await doWork();
 *   if (!runs.isCurrent(run)) return;         // after EVERY await, before any setState
 *   runs.cancel();                            // Stop, Reset, "new file", file chosen, option changed
 *
 * This replaces the shared "cancelled" boolean that the next run used to flip back to false, which
 * let a stopped run carry on and deliver its result under a different file's name.
 */
export function useRunToken() {
  const ref = useRef<ReturnType<typeof createRunToken> | null>(null);
  if (!ref.current) ref.current = createRunToken();
  return ref.current;
}
